import builtInValidators, { TValidatorFn } from './validators';

export type TValidatorSpec<D = any> = {
  [key: string]: any;
  /** The function used to validate the data */
  fn?: TValidatorFn<D>;
  /** The message to display if the validation fails */
  msg?: string;
  /** The name of the key to validate in the data */
  name?: string | ((data?: D) => string);
  param?: unknown;
};

export type TValidatorSpecParam<D = any> =
  | TValidatorFn<D>
  | TValidatorFn<D>[]
  | TValidatorSpec<D>
  | TValidatorSpec<D>[];

export type TValidatorHash<D = any> = Record<
  keyof D,
  Record<string, TValidatorSpecParam<D>> | TValidatorSpecParam<D>
>;

const parseValidators = {
  getValidatorListFromSpec<D = any>(validatorSpec: TValidatorSpecParam<D>) {
    const validatorSpecs = Array.isArray(validatorSpec) ? validatorSpec : [validatorSpec];
    const validators: TValidatorSpec<D>[] = [];

    validatorSpecs.forEach((validator) => {
      let newValidator: TValidatorSpec<D>;
      if (typeof validator === 'function') {
        // eslint-disable-next-line no-param-reassign
        newValidator = {
          // eslint-disable-line no-param-reassign
          fn: validator,
        };
      } else {
        newValidator = validator;
      }

      // all validators specified for a property are boiled down into array of functions
      Object.keys(newValidator)
        .filter((key) => key !== 'name' && key !== 'msg')
        .forEach((validatorName) => {
          const methodKey = validatorName as keyof typeof builtInValidators.functions;
          const paramKey = validatorName as keyof typeof newValidator;
          const validateObj = {
            fn: validatorName === 'fn' ? newValidator.fn : builtInValidators.functions[methodKey],
            msg: newValidator.msg || builtInValidators.messages[validatorName],
            name: newValidator.name,
            param: validatorName === 'fn' ? undefined : (newValidator[paramKey] as string),
          };

          validators.push(validateObj);
        });
    }, this);

    return validators;
  },

  getValidators<D = any>(validatorHash?: TValidatorHash<D>, key?: keyof D) {
    const validators: Record<string, TValidatorSpec[]> = {};

    if (validatorHash) {
      Object.keys(validatorHash).forEach((currentAttr) => {
        const validatorSpec = validatorHash[currentAttr as keyof D];

        if ((typeof key === 'undefined' || key === currentAttr) && validatorSpec) {
          validators[currentAttr] = this.getValidatorListFromSpec(validatorSpec);
        }
      }, this);
    }

    return validators;
  },
};

export default parseValidators;
